import React, { useState, useEffect, useCallback } from "react";
import "../../../../assets/css/purchase/datatable.css";
import Axios from "axios";

// Components
import Breadcrumbs from "../../../components/Breadcrumbs";
import Topbar from "../../../components/Topbar";

// Date & Time
import moment from "moment";

// Utils
import { getAlert, formatCurrency, parseCurrency, getDataWithComp, getData, deleteData, updateStatusByNo } from "../../../../utils/SamuiUtils";
import { formatDate } from "../../../../utils/DateUtils";
import SearchAndFilter from "../../../components/Search/SearchAndFilter";
import Pagination from "../../../components/Footer/Pagination";
import FileModal from "../../../components/Modal/FileModal";

const PER_PAGE_KEY = 'paymentVoucherItemsPerPage';
const SELECTED_ROW_KEY = 'paymentVoucherSelectedRow';

const PaymentVoucherTable = ({
  currentItems,
  onRowSelected,
  currentPage,
  setCurrentPage,
  handlePageChange,
  dataMasterList,
  itemsPerPage,
  setItemsPerPage,
  getReport,
  statusOptions = [],
  statusField = 'Pay_Status',
  onStatusChange,
  isStatusEditable = () => true,
  sortConfig,
  onSort,
}) => {
  const [itemsPerPageDropdown, setItemsPerPageDropdown] = useState(() => {
    const stored = Number(localStorage.getItem(PER_PAGE_KEY));
    return stored > 0 ? stored : Number(itemsPerPage) || 10;
  });
  const [selectedRow, setSelectedRow] = useState(() => localStorage.getItem(SELECTED_ROW_KEY));
  const [selectedRowIndex, setSelectedRowIndex] = useState(0);
  const [showFileModal, setShowFileModal] = useState(false);
  const [fileModalData, setFileModalData] = useState({ files: [], recFiles: [] });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [isConfirmBusy, setIsConfirmBusy] = useState(false);

  useEffect(() => {
    setItemsPerPage(itemsPerPageDropdown);
    localStorage.setItem(PER_PAGE_KEY, String(itemsPerPageDropdown));
  }, [itemsPerPageDropdown, setItemsPerPage]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        document.activeElement.tagName === 'INPUT' ||
        document.activeElement.tagName === 'TEXTAREA' ||
        document.activeElement.tagName === 'SELECT'
      ) {
        return;
      }

      if (currentItems.length === 0) return;

      switch (event.key) {
        case 'ArrowUp':
          event.preventDefault();
          setSelectedRowIndex((prev) => {
            const newIndex = prev > 0 ? prev - 1 : currentItems.length - 1;
            const newRowId = currentItems[newIndex]?.Pay_No || newIndex + 1;
            setSelectedRow(newRowId);
            localStorage.setItem(SELECTED_ROW_KEY, newRowId);
            return newIndex;
          });
          break;
        case 'ArrowDown':
          event.preventDefault();
          setSelectedRowIndex((prev) => {
            const newIndex = prev < currentItems.length - 1 ? prev + 1 : 0;
            const newRowId = currentItems[newIndex]?.Pay_No || newIndex + 1;
            setSelectedRow(newRowId);
            localStorage.setItem(SELECTED_ROW_KEY, newRowId);
            return newIndex;
          });
          break;
        case 'Enter':
          event.preventDefault();
          if (currentItems[selectedRowIndex]) {
            onRowSelected(currentItems[selectedRowIndex].Pay_No);
          }
          break;
        case 'P':
        case 'p':
          event.preventDefault();
          if (currentItems[selectedRowIndex] && getReport) {
            const row = currentItems[selectedRowIndex];
            const status = row.Pay_Status ?? row.payStatus ?? row.PayStatus;
            if (String(status) === '13') return;
            getReport(currentItems[selectedRowIndex].Pay_No);
          }
          break;
        case 'ArrowLeft':
          event.preventDefault();
          if (currentPage > 1) {
            handlePageChange(currentPage - 1);
          }
          break;
        case 'ArrowRight':
          event.preventDefault();
          const totalPages = Math.ceil(dataMasterList.length / itemsPerPageDropdown);
          if (currentPage < totalPages) {
            handlePageChange(currentPage + 1);
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentItems, selectedRowIndex, currentPage, dataMasterList.length, itemsPerPageDropdown, handlePageChange, onRowSelected, getReport]);

  useEffect(() => {
    if (selectedRow && currentItems.length > 0) {
      const index = currentItems.findIndex((item) => item.Pay_No === selectedRow);
      if (index !== -1) {
        setSelectedRowIndex(index);
      }
    }
  }, [selectedRow, currentItems]);

  const handleItemsPerPageChange = (event) => {
    const newVal = Number(event.target.value) || 10;
    setItemsPerPageDropdown(newVal);
    handlePageChange(1);
    setCurrentPage(1);
  };

  const handleViewFiles = (files, additionalFiles = []) => {
    if ((!Array.isArray(files) || files.length === 0) && (!Array.isArray(additionalFiles) || additionalFiles.length === 0)) {
      return;
    }
    setFileModalData({ files: files || [], recFiles: additionalFiles || [] });
    setShowFileModal(true);
  };

  const startIndex = dataMasterList.length === 0 ? 0 : (currentPage - 1) * itemsPerPageDropdown + 1;
  const endIndex = Math.min(currentPage * itemsPerPageDropdown, dataMasterList.length);

  const renderStatusControl = (rowData) => {
    if (!Array.isArray(statusOptions) || statusOptions.length === 0 || typeof onStatusChange !== 'function') {
      if (!rowData.PayStatus_Name) {
        return (
          <center>
            <p>-</p>
          </center>
        );
      }
      return (
        <button
          className="btn"
          style={{
            backgroundColor: rowData.PayStatus_Colour || '#6c757d',
            color: 'white',
            width: '100%',
          }}
        >
          {rowData.PayStatus_Name}
        </button>
      );
    }

    const rawValue = rowData[statusField];
    const normalizedValue = rawValue !== undefined && rawValue !== null ? String(rawValue) : '';
    const selectedOption = statusOptions.find((option) => String(option.value) === normalizedValue);
    const isEditable = isStatusEditable(rowData, selectedOption);

    const handleRequestChange = (newValue) => {
      const toOption = statusOptions.find((o) => String(o.value) === String(newValue));
      setPendingStatusChange({
        id: rowData.Pay_No,
        newValue,
        rowData,
        fromOption: selectedOption,
        toOption,
      });
      setShowConfirmModal(true);
    };

    return (
      <select
        className="form-select form-select-sm"
        value={selectedOption ? String(selectedOption.value) : normalizedValue}
        disabled={!isEditable}
        onChange={(event) => handleRequestChange(event.target.value)}
        style={{
          backgroundColor: (selectedOption?.color || rowData.PayStatus_Colour || '#6c757d'),
          color: '#fff',
          fontWeight: 600,
          borderRadius: '25px',
          border: 'none',
          paddingLeft: '12px',
          paddingRight: '24px',
          cursor: isEditable ? 'pointer' : 'not-allowed',
        }}
      >
        {statusOptions.map((option) => (
          <option key={`${rowData.Pay_No}-${option.value}`} value={String(option.value)} style={{ color: '#000' }}>
            {option.label}
          </option>
        ))}
      </select>
    );
  };

  return (
    <div className="card table-card">
      <div className="table-responsive table-wrapper">
        <table className="table table-striped table-hover">
          <thead className="thead-dark">
            <tr>
              <th className="text-center" style={{ width: '8%', cursor: 'pointer' }} onClick={() => onSort && onSort('Pay_Date')}>
                <span className="d-inline-flex align-items-center justify-content-center w-100">
                  วันที่ {(!sortConfig || sortConfig.key !== 'Pay_Date') ? (
                    <svg className="w-3.5 h-3.5 opacity-60 ms-1" viewBox="0 0 20 20" fill="currentColor"><path d="M5 8l5-5 5 5H5zM15 12l-5 5-5-5h10z" /></svg>
                  ) : sortConfig.direction === 'asc' ? (
                    <svg className="w-3.5 h-3.5 opacity-90 ms-1" viewBox="0 0 20 20" fill="currentColor"><path d="M5 12l5-5 5 5H5z" /></svg>
                  ) : (
                    <svg className="w-3.5 h-3.5 opacity-90 ms-1" viewBox="0 0 20 20" fill="currentColor"><path d="M15 8l-5 5-5-5h10z" /></svg>
                  )}
                </span>
              </th>
              <th className="text-center" style={{ width: '12%', cursor: 'pointer' }} onClick={() => onSort && onSort('Pay_No')}>
                <span className="d-inline-flex align-items-center justify-content-center w-100">
                  เลขที่เอกสาร {(!sortConfig || sortConfig.key !== 'Pay_No') ? (
                    <svg className="w-3.5 h-3.5 opacity-60 ms-1" viewBox="0 0 20 20" fill="currentColor"><path d="M5 8l5-5 5 5H5zM15 12l-5 5-5-5h10z" /></svg>
                  ) : sortConfig.direction === 'asc' ? (
                    <svg className="w-3.5 h-3.5 opacity-90 ms-1" viewBox="0 0 20 20" fill="currentColor"><path d="M5 12l5-5 5 5H5z" /></svg>
                  ) : (
                    <svg className="w-3.5 h-3.5 opacity-90 ms-1" viewBox="0 0 20 20" fill="currentColor"><path d="M15 8l-5 5-5-5h10z" /></svg>
                  )}
                </span>
              </th>
              <th className="text-center" style={{ width: '12%' }}>
                <span className="d-inline-flex align-items-center justify-content-center w-100">
                  ประเภทจ่าย
                </span>
              </th>
              <th className="text-left" style={{ width: '20%', cursor: 'pointer' }} onClick={() => onSort && onSort('AP_Name')}>
                <span className="d-inline-flex align-items-center">ชื่อผู้จำหน่าย {(!sortConfig || sortConfig.key !== 'AP_Name') ? (
                  <svg className="w-3.5 h-3.5 opacity-60 ms-1" viewBox="0 0 20 20" fill="currentColor"><path d="M5 8l5-5 5 5H5zM15 12l-5 5-5-5h10z" /></svg>
                ) : sortConfig.direction === 'asc' ? (
                  <svg className="w-3.5 h-3.5 opacity-90 ms-1" viewBox="0 0 20 20" fill="currentColor"><path d="M5 12l5-5 5 5H5z" /></svg>
                ) : (
                  <svg className="w-3.5 h-3.5 opacity-90 ms-1" viewBox="0 0 20 20" fill="currentColor"><path d="M15 8l-5 5-5-5h10z" /></svg>
                )}</span>
              </th>
              <th className="text-center" style={{ width: '10%', cursor: 'pointer' }} onClick={() => onSort && onSort('Doc_DueDate')}>
                <span className="d-inline-flex align-items-center justify-content-center w-100">
                  วันที่ชำระ {(!sortConfig || sortConfig.key !== 'Doc_DueDate') ? (
                    <svg className="w-3.5 h-3.5 opacity-60 ms-1" viewBox="0 0 20 20" fill="currentColor"><path d="M5 8l5-5 5 5H5zM15 12l-5 5-5-5h10z" /></svg>
                  ) : sortConfig.direction === 'asc' ? (
                    <svg className="w-3.5 h-3.5 opacity-90 ms-1" viewBox="0 0 20 20" fill="currentColor"><path d="M5 12l5-5 5 5H5z" /></svg>
                  ) : (
                    <svg className="w-3.5 h-3.5 opacity-90 ms-1" viewBox="0 0 20 20" fill="currentColor"><path d="M15 8l-5 5-5-5h10z" /></svg>
                  )}
                </span>
              </th>
              <th className="text-center" style={{ width: '10%', cursor: 'pointer' }} onClick={() => onSort && onSort('Total_Pay_NetTotal')}>
                <span className="d-inline-flex align-items-center justify-content-center w-100">
                  ยอดรวมสุทธิ {(!sortConfig || sortConfig.key !== 'Total_Pay_NetTotal') ? (
                    <svg className="w-3.5 h-3.5 opacity-60 ms-1" viewBox="0 0 20 20" fill="currentColor"><path d="M5 8l5-5 5 5H5zM15 12l-5 5-5-5h10z" /></svg>
                  ) : sortConfig.direction === 'asc' ? (
                    <svg className="w-3.5 h-3.5 opacity-90 ms-1" viewBox="0 0 20 20" fill="currentColor"><path d="M5 12l5-5 5 5H5z" /></svg>
                  ) : (
                    <svg className="w-3.5 h-3.5 opacity-90 ms-1" viewBox="0 0 20 20" fill="currentColor"><path d="M15 8l-5 5-5-5h10z" /></svg>
                  )}
                </span>
              </th>
              <th className="text-center" style={{ width: '10%' }}>สถานะ</th>
              <th className="text-center" style={{ width: '5%' }}>ไฟล์</th>
              <th className="text-center" style={{ width: '5%' }}>แก้ไข</th>
              <th className="text-center" style={{ width: '5%' }}>พิมพ์</th>
            </tr>
          </thead>
          <tbody>
            {currentItems.length > 0 ? (
              currentItems.map((item, index) => (
                <tr
                  key={item.Pay_No || index}
                  style={{
                    cursor: 'pointer',
                    backgroundColor: selectedRow === (item.Pay_No || index)
                      ? 'rgb(105, 188, 216)'
                      : 'transparent',
                    transition: 'background-color 0.3s ease',
                  }}
                  onClick={() => {
                    setSelectedRow(item.Pay_No || index);
                    setSelectedRowIndex(index);
                    localStorage.setItem(SELECTED_ROW_KEY, item.Pay_No || index);
                  }}
                >
                  <td className="text-center">{item.Pay_Date ? formatDate(item.Pay_Date) : '-'}</td>
                  <td className="text-center">{item.Pay_No || '-'}</td>
                  <td className="text-center">{item.Pay_TypeName || '-'}</td>
                  <td className="text-left">{item.AP_Name || '-'}</td>
                  <td className="text-center">{item.Doc_DueDate ? formatDate(item.Doc_DueDate) : '-'}</td>
                  <td className="text-end">{formatCurrency(parseCurrency(item.Total_Pay_NetTotal) || 0)}</td>
                  <td className="text-left">{renderStatusControl(item)}</td>
                  <td className="text-center">
                    {Array.isArray(item.File_Name) && item.File_Name.length > 0 ? (
                      <button
                        className="btn btn-link btn-sm px-3"
                        onClick={() => handleViewFiles(item.File_Name, item.File_Name_Rec)}
                      >
                        <i className="fas fa-paperclip" style={{ fontSize: '16px', color: 'rgb(239, 108, 0)' }}></i>
                      </button>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="text-center">
                    <button
                      className="btn btn-link btn-sm px-3"
                      onClick={() => onRowSelected(item.Pay_No)}
                    >
                      <i className="fa fa-edit" style={{ fontSize: '16px', color: 'rgb(239, 108, 0)' }}></i>
                    </button>
                  </td>
                  <td className="text-center">
                    {(() => {
                      const status = item.Pay_Status ?? item.payStatus ?? item.PayStatus;
                      const isCancelled = String(status) === '13';
                      return (
                        <button
                          className="btn btn-link btn-sm px-3"
                          onClick={() => {
                            if (isCancelled) return;
                            getReport(item.Pay_No);
                          }}
                          disabled={isCancelled}
                          style={isCancelled ? { cursor: 'not-allowed', opacity: 0.5 } : {}}
                        >
                          <i className="fa fa-print" style={{ fontSize: '16px' }}></i>
                        </button>
                      );
                    })()}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={10}>
                  <center>
                    <h5>ไม่พบข้อมูล</h5>
                  </center>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="table-footer-sticky d-flex justify-content-between align-items-center p-3">
        <div>
          <select
            value={itemsPerPageDropdown}
            onChange={handleItemsPerPageChange}
            className="form-select"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={30}>30</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
        <div>
          <span>
            แสดง {startIndex}-{endIndex} ของ {dataMasterList.length.toLocaleString()} รายการ
          </span>
        </div>
        <div className="card-footer bg-light p-2">
          <small className="text-muted">
            <i className="fas fa-keyboard me-1"></i>
            <strong>คีย์ลัด:</strong> ↑↓ เลือกแถว | ←→ เปลี่ยนหน้า | Enter เข้าดู | P พิมพ์
          </small>
        </div>
        <Pagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          itemsPerPage={itemsPerPageDropdown}
          dataMasterList={dataMasterList}
          handlePageChange={handlePageChange}
        />
      </div>

      {showFileModal && (
        <FileModal
          mode="S"
          showViewAttachModal={showFileModal}
          setShowViewAttachModal={setShowFileModal}
          selectedFiles={fileModalData.files}
          selectedRecFiles={fileModalData.recFiles}
        />
      )}

      {/* Confirm Status Change Modal */}
      <div className={`modal fade ${showConfirmModal ? 'show' : ''}`} tabIndex="-1" style={{ display: showConfirmModal ? 'block' : 'none' }}>
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title fw-bold">ยืนยันการเปลี่ยนสถานะ</h5>
              <button type="button" className="btn-close" onClick={() => setShowConfirmModal(false)} aria-label="Close"></button>
            </div>
            <div className="modal-body">
              <p>
                คุณแน่ใจว่าต้องการเปลี่ยนสถานะจาก
                <strong className="mx-1">{pendingStatusChange?.fromOption?.label || '-'}</strong>
                เป็น
                <strong className="mx-1">{pendingStatusChange?.toOption?.label || '-'}</strong>
                หรือไม่?
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-lg w-25 shadow text-white d-flex align-items-center justify-content-center"
                style={{ backgroundColor: '#E13F57', fontSize: '16px' }}
                onClick={async () => {
                  if (!pendingStatusChange || typeof onStatusChange !== 'function') {
                    setShowConfirmModal(false);
                    return;
                  }
                  try {
                    setIsConfirmBusy(true);
                    await Promise.resolve(onStatusChange(pendingStatusChange.id, pendingStatusChange.newValue, pendingStatusChange.rowData));
                  } finally {
                    setIsConfirmBusy(false);
                    setShowConfirmModal(false);
                    setPendingStatusChange(null);
                  }
                }}
                disabled={isConfirmBusy}
              >
                {isConfirmBusy ? (
                  <>
                    <i className="fa fa-spinner fa-spin me-2" style={{ fontSize: '20px', color: '#ffffff' }} aria-hidden="true"></i>
                    กำลังโหลด
                  </>
                ) : (
                  'ยืนยัน'
                )}
              </button>
              <button
                type="button"
                className="btn btn-lg w-30 shadow text-white"
                style={{ backgroundColor: 'red', fontSize: '16px' }}
                onClick={() => setShowConfirmModal(false)}
                disabled={isConfirmBusy}
              >
                ยกเลิก
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function Main({
  masterList,
  detailList,
  statusColours,
  name,
  onPageInsert,
  onRowSelected,
  getReport,
  statusOptions = [],
  onStatusChange,
  isStatusEditable,
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const stored = Number(localStorage.getItem(PER_PAGE_KEY));
    return stored > 0 ? stored : Number(localStorage.getItem('itemsPerPageDropdown')) || 10;
  });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState(
    localStorage.getItem("searchTerm") || ""
  );
  const [filteredItems, setFilteredItems] = useState([]);

  const [startDate, setStartDate] = useState(() => {
    const storedStartDate = localStorage.getItem("startDate");
    return storedStartDate || moment().startOf("month").format("YYYY-MM-DD");
  });
  const [endDate, setEndDate] = useState(() => {
    const storedEndDate = localStorage.getItem("endDate");
    return storedEndDate || moment().endOf("month").format("YYYY-MM-DD");
  });

  const [selectedStatus] = useState(null);
  const [showPaymentChannelModal, setShowPaymentChannelModal] = useState(false);
  const [pendingPaymentContext, setPendingPaymentContext] = useState(null);
  const [modalPaymentType, setModalPaymentType] = useState({
    cash: false,
    transfer: false,
    cheque: false,
  });
  const [modalPaymentAmount, setModalPaymentAmount] = useState({
    cash: "0.00",
    transfer: "0.00",
    cheque: "0.00",
  });
  const [modalPayDatetime, setModalPayDatetime] = useState(
    moment().format("YYYY-MM-DD")
  );
  const [modalChequeNo, setModalChequeNo] = useState("");
  const [modalChequeName, setModalChequeName] = useState("");
  const [modalChequeBranch, setModalChequeBranch] =
    useState("ธนาคารกสิกรไทย");
  const [modalChequeDate, setModalChequeDate] = useState("");
  const [modalBankInputMode, setModalBankInputMode] = useState("select");
  const [modalBankMasterId, setModalBankMasterId] = useState("");
  const [modalBankId, setModalBankId] = useState("");
  const [modalPayToAccNo, setModalPayToAccNo] = useState("");
  const [modalPayToAccName, setModalPayToAccName] = useState("");
  const [modalPayToBranch, setModalPayToBranch] = useState("");
  const [modalBankMasterList, setModalBankMasterList] = useState([]);
  const [modalBankList, setModalBankList] = useState([]);
  const [modalDocTotal, setModalDocTotal] = useState(0);
  const [isSavingPayment, setIsSavingPayment] = useState(false);

  const resetModalPayment = (rowData = null) => {
    setModalPaymentType({ cash: false, transfer: false, cheque: false });
    setModalPaymentAmount({ cash: "0.00", transfer: "0.00", cheque: "0.00" });
    setModalPayDatetime(moment().format("YYYY-MM-DD"));
    setModalChequeNo("");
    setModalChequeName("");
    setModalChequeBranch("ธนาคารกสิกรไทย");
    setModalChequeDate("");
    setModalBankInputMode("select");
    setModalBankMasterId("");
    setModalBankId("");
    setModalPayToAccNo("");
    setModalPayToAccName("");
    setModalPayToBranch("");
    const total =
      (rowData && parseCurrency(rowData.Total_Pay_NetTotal)) || 0;
    setModalDocTotal(total || 0);
  };

  const toggleModalPaymentMethod = (method) => {
    setModalPaymentType((prev) => {
      const next = { ...prev, [method]: !prev[method] };
      setModalPaymentAmount((prevAmount) => {
        const nextAmount = { ...prevAmount };
        if (next[method]) {
          const activeMethods = Object.keys(next).filter((key) => next[key]);
          if (activeMethods.length === 1) {
            nextAmount[method] = formatCurrency(modalDocTotal || 0);
          } else if (!nextAmount[method] || nextAmount[method] === "0.00") {
            nextAmount[method] = "0.00";
          }
        } else {
          nextAmount[method] = "0.00";
        }
        return nextAmount;
      });

      if (method === "transfer" && !next[method]) {
        setModalBankId("");
        setModalBankMasterId("");
        setModalPayToAccNo("");
        setModalPayToAccName("");
        setModalPayToBranch("");
        setModalBankInputMode("select");
      }

      if (method === "cheque" && !next[method]) {
        setModalChequeNo("");
        setModalChequeName("");
        setModalChequeBranch("ธนาคารกสิกรไทย");
        setModalChequeDate("");
      }

      return next;
    });
  };

  const handleModalAmountChange = (method, rawValue) => {
    const sanitized = rawValue.replace(/[^0-9.]/g, "");
    setModalPaymentAmount((prev) => ({
      ...prev,
      [method]: sanitized,
    }));
  };

  const handleModalAmountFocus = (method) => {
    setModalPaymentAmount((prev) => {
      const numeric = parseCurrency(prev[method] || 0) || 0;
      return {
        ...prev,
        [method]: String(numeric),
      };
    });
  };

  const handleModalAmountBlur = (method) => {
    setModalPaymentAmount((prev) => {
      const numeric = parseCurrency(prev[method] || 0) || 0;
      return {
        ...prev,
        [method]: formatCurrency(numeric),
      };
    });
  };

  const getModalSelectedTotal = () =>
    Object.entries(modalPaymentType).reduce((acc, [method, selected]) => {
      if (!selected) {
        return acc;
      }
      const amount = parseCurrency(modalPaymentAmount[method] || 0) || 0;
      return acc + amount;
    }, 0);

  const formatAccountNo = (value) => {
    if (!value) return "";
    const digits = String(value).replace(/\D/g, "").slice(0, 10);
    const p1 = digits.slice(0, 3);
    const p2 = digits.slice(3, 6);
    const p3 = digits.slice(6, 10);
    return [p1, p2, p3].filter(Boolean).join("-");
  };

  const handleModalAccNoChange = (raw) => {
    const formatted = formatAccountNo(raw);
    setModalPayToAccNo(formatted);
  };

  const handleModalAccNoBlur = () => {
    const digits = String(modalPayToAccNo).replace(/\D/g, "");
    if (modalPayToAccNo && digits.length !== 10) {
      try {
        getAlert("WARNING", "รูปแบบเลขที่บัญชีต้องเป็น xxx-xxx-xxxx");
      } catch (e) {
        console.warn("Invalid account format");
      }
    }
  };

  useEffect(() => {
    const loadBankData = async () => {
      try {
        const masterBanks = await getData(
          "Tb_Set_Bank_Master",
          "1 = 1 ORDER BY Bank_Master_Id ASC"
        );
        if (Array.isArray(masterBanks)) {
          setModalBankMasterList(masterBanks);
        }

        const bankCondition =
          "AND Bank_Status = 'Y' AND APAR_Type = 'AP' ORDER BY Bank_Id ASC";
        const bankList = await getDataWithComp(
          "Tb_Set_Bank",
          bankCondition
        );
        if (bankList) {
          const rawList = Array.isArray(bankList)
            ? bankList
            : Array.isArray(bankList.data)
              ? bankList.data
              : [];

          const normalized = rawList.map((b) => ({
            Bank_Id: b.Bank_Id ?? b.BankId ?? b.id ?? b.BankID ?? "",
            Bank_Account: b.Bank_Account ?? b.Account ?? b.bank_account ?? b.BankAccount ?? "",
            Bank_Name_TH: b.Bank_Name_TH ?? b.Bank_Name ?? b.B_Name_TH ?? b.BankName ?? "",
            __raw: b,
          }));

          if (normalized.length > 0) {
            setModalBankList(normalized);
          } else {
            if (Array.isArray(bankList)) setModalBankList(bankList);
          }
        }
      } catch (error) {
        console.error("โหลดข้อมูลบัญชีธนาคารไม่สำเร็จ:", error);
      }
    };

    loadBankData();
  }, []);
  const persistModalPaymentHistory = async (payId) => {
    const selectedMethods = Object.entries(modalPaymentType).filter(
      ([, selected]) => selected
    );

    if (selectedMethods.length === 0) {
      throw new Error("PAYMENT_METHOD_REQUIRED");
    }

    const hasAmount = selectedMethods.some(
      ([method]) => (parseCurrency(modalPaymentAmount[method] || 0) || 0) > 0
    );

    if (!hasAmount) {
      throw new Error("PAYMENT_METHOD_REQUIRED");
    }

    await deleteData(
      "Pay_History",
      `WHERE DocCode_Id = 4 AND Doc_Id = ${payId}`
    );

    const paymentTypeMap = {
      cash: 1,
      transfer: 2,
      cheque: 3,
    };

    for (const [method] of selectedMethods) {
      const numericAmount = parseCurrency(
        modalPaymentAmount[method] || 0
      ) || 0;

      if (numericAmount <= 0) {
        continue;
      }

      let bankMasterValue = null;
      let bankIdValue = null;
      let payToAccNoValue = null;
      let payToAccNameValue = null;
      let payToBranchValue = null;

      if (method === "transfer" || method === "cheque") {
        if (modalBankInputMode === "select" && modalBankId) {
          const parsedBankId = Number(modalBankId);
          bankIdValue = Number.isNaN(parsedBankId) ? null : parsedBankId;
        } else {
          const parsedBankMasterId = Number(modalBankMasterId);
          bankMasterValue = Number.isNaN(parsedBankMasterId)
            ? null
            : parsedBankMasterId;
          payToAccNoValue = modalPayToAccNo || null;
          payToAccNameValue = modalPayToAccName || null;
          payToBranchValue = modalPayToBranch || null;
        }

        if (!payToAccNoValue) {
          payToAccNoValue = modalPayToAccNo || null;
        }
        if (!payToAccNameValue) {
          payToAccNameValue = modalPayToAccName || null;
        }
        if (!payToBranchValue) {
          payToBranchValue = modalPayToBranch || null;
        }
      }

      const isCheque = paymentTypeMap[method] === 3;
      const datetimeSource =
        isCheque && modalChequeDate ? modalChequeDate : modalPayDatetime;

      const payload = {
        doc_code_id: 4,
        doc_id: payId,
        pay_type: paymentTypeMap[method],
        pay_amount: numericAmount,
        pay_remark: null,
        pay_datetime: moment(datetimeSource || new Date())
          .utcOffset(7, true)
          .format("YYYY-MM-DD HH:mm:ss.SSS"),
        pay_by: window.localStorage.getItem("name"),
        pay_to_acc_no: payToAccNoValue,
        pay_to_branch: payToBranchValue,
        pay_to_acc_name: payToAccNameValue,
        pay_cheque_no: isCheque ? modalChequeNo || null : null,
        pay_cheque_name: isCheque ? modalChequeName || null : null,
        pay_cheque_branch: isCheque
          ? modalChequeBranch || "ธนาคารกสิกรไทย"
          : null,
        pay_cheque_date:
          isCheque && modalChequeDate
            ? moment(modalChequeDate)
              .utcOffset(7, true)
              .format("YYYY-MM-DD HH:mm:ss.SSS")
            : null,
        pay_cheque_type: null,
        comp_id: window.localStorage.getItem("company"),
        bank_master_id: bankMasterValue,
        bank_id: bankIdValue,
        doc_ref: null,
      };

      await Axios.post(
        `${process.env.REACT_APP_API_URL}/api/create-pay-history`,
        payload,
        {
          headers: { key: process.env.REACT_APP_ANALYTICS_KEY },
        }
      );
    }
  };

  const handleStatusChange = async (
    payNo,
    nextStatusValue,
    rowData,
    options = {}
  ) => {
    if (typeof onStatusChange !== "function") {
      return;
    }

    const skipValidation = options.skipPaymentValidation;
    if (Number(nextStatusValue) === 2 && !skipValidation) {
      try {
        const payId = rowData?.Pay_Id;
        if (!payId) {
          getAlert("FAILED", "ไม่พบรหัสใบสำคัญจ่าย");
          return;
        }

        const payHistory = await getData(
          "Pay_History",
          `DocCode_Id = 4 AND Doc_Id = ${payId}`
        );

        if (!Array.isArray(payHistory) || payHistory.length === 0) {
          resetModalPayment(rowData);
          setPendingPaymentContext({
            payNo,
            nextStatusValue,
            rowData,
            payId,
          });
          setShowPaymentChannelModal(true);
          return;
        }
      } catch (error) {
        getAlert("FAILED", error?.response?.data?.message || error.message);
        return;
      }
    }

    // เรียกใช้ฟังก์ชันเปลี่ยนสถานะหลัก
    await onStatusChange(payNo, nextStatusValue, rowData);

    // หลังจากเปลี่ยนสถานะสำเร็จแล้ว ให้ UPDATE สถานะการจ่ายของ REC_H
    try {
      const payId = rowData?.Pay_Id;
      const payType = rowData?.Pay_Type;

      // เฉพาะกรณีที่เป็นใบจ่ายจากใบรับ (Pay_Type = 1)
      if (payId && Number(payType) === 1) {
        // ดึงข้อมูล PAY_D เพื่อหา Rec_Id
        const payDetails = await getData("PAY_D", `Pay_Id = ${payId}`);

        if (Array.isArray(payDetails) && payDetails.length > 0) {
          // เตรียมค่า Doc_Status_Paid ตามสถานะที่เปลี่ยน
          // ถ้าอนุมัติ (nextStatusValue = 2) -> Doc_Status_Paid = 2 (จ่ายแล้ว)
          // ถ้าไม่ใช่อนุมัติ -> Doc_Status_Paid = 1 (รอจ่าย)
          const docStatusPaid = Number(nextStatusValue) === 2 ? 2 : 1;

          // UPDATE แต่ละ REC_H ที่เกี่ยวข้อง
          for (const detail of payDetails) {
            if (detail.Rec_Id) {
              await updateStatusByNo(
                "REC_H",
                "Doc_Status_Paid",
                docStatusPaid,
                `WHERE Rec_Id = '${detail.Rec_Id}'`
              );
            }
          }
        }
      }
    } catch (error) {
      console.error("Error updating REC_H Doc_Status_Paid:", error);
    }
  };

  const handlePaymentModalConfirm = async () => {
    if (!pendingPaymentContext) {
      return;
    }

    try {
      setIsSavingPayment(true);

      const totalSelected = getModalSelectedTotal() || 0;
      const docTotal = modalDocTotal || 0;
      if (Math.abs(totalSelected - docTotal) >= 0.01) {
        getAlert("FAILED", "ต้องจ่ายตามจำนวนให้ถูกด้วย");
        setIsSavingPayment(false);
        return;
      }

      await persistModalPaymentHistory(pendingPaymentContext.payId);
      await handleStatusChange(
        pendingPaymentContext.payNo,
        pendingPaymentContext.nextStatusValue,
        pendingPaymentContext.rowData,
        { skipPaymentValidation: true }
      );
      setShowPaymentChannelModal(false);
      setPendingPaymentContext(null);
      resetModalPayment();
    } catch (error) {
      if (error?.message === "PAYMENT_METHOD_REQUIRED") {
        getAlert("FAILED", "กรุณาเลือกช่องทางการชำระและระบุจำนวนเงิน");
      } else {
        getAlert(
          "FAILED",
          error?.response?.data?.message ||
          error.message ||
          "บันทึกช่องทางการชำระไม่สำเร็จ"
        );
      }
    } finally {
      setIsSavingPayment(false);
    }
  };

  const handleClosePaymentModal = () => {
    setShowPaymentChannelModal(false);
    setPendingPaymentContext(null);
    resetModalPayment();
  };
  const initialize = useCallback(async () => {
    try {
      if (masterList && masterList.length > 0) {
        const image = await getDataWithComp(
          "Upload_File_List",
          `AND DocCode_ID IN (4)`
        );

        const updatedMasterList = await Promise.all(masterList.map(async (item) => {
          const relatedFiles = image.filter(
            (img) => img.Doc_ID === item.Pay_Id
          );

          let relatedFilesRec = [];

          if (Number(item.Pay_Type) === 1) {
            const getDetailForRec = await getData("PAY_D", `Pay_Id = ${item.Pay_Id}`);
            if (getDetailForRec && getDetailForRec.length > 0) {
              const imageRec = await getDataWithComp(
                "Upload_File_List",
                `AND DocCode_ID = 3 AND Doc_ID = ${getDetailForRec[0].Rec_Id}`
              );
              relatedFilesRec = imageRec;
            }
          }

          return {
            ...item,
            File_Name: relatedFiles,
            File_Name_Rec: relatedFilesRec,
          };
        }));

        setFilteredItems(updatedMasterList);
        setCurrentPage(1);
      }
    } catch (error) {
      getAlert("FAILED", error.message);
    }
  }, [masterList]);

  useEffect(() => {
    initialize();
  }, [initialize]);
  const filterItems = useCallback(() => {
    let filtered = masterList || [];

    const start = moment(startDate, "YYYY-MM-DD");
    const end = moment(endDate, "YYYY-MM-DD");

    filtered = filtered.filter((item) => {
      const itemDate = moment(item.Pay_Date, "YYYY-MM-DD");
      return itemDate.isBetween(start, end, null, "[]");
    });

    if (searchTerm.trim() !== "") {
      const lowercasedSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter((item) =>
        Object.values(item).some((value) => {
          if (typeof value === "string") {
            return value.toLowerCase().includes(lowercasedSearchTerm);
          } else if (typeof value === "number") {
            return value.toString().includes(searchTerm);
          }
          return false;
        })
      );
    }

    if (selectedStatus) {
      filtered = filtered.filter((item) => item.PayStatus_Name === selectedStatus);
    }

    setFilteredItems(filtered);
  }, [masterList, startDate, endDate, searchTerm, selectedStatus]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  useEffect(() => {
    setCurrentPage(1);
    filterItems();
  }, [filterItems]);

  const sortedItems = React.useMemo(() => {
    if (!sortConfig || !sortConfig.key) return filteredItems;
    const key = sortConfig.key;
    const direction = sortConfig.direction === 'desc' ? -1 : 1;
    const copy = [...filteredItems];
    copy.sort((a, b) => {
      const av = a[key];
      const bv = b[key];

      const dateKeys = ['Pay_Date', 'Doc_DueDate'];
      if (dateKeys.includes(key)) {
        const am = av ? moment(av) : null;
        const bm = bv ? moment(bv) : null;
        if (!am && !bm) return 0;
        if (!am) return 1 * direction;
        if (!bm) return -1 * direction;
        if (am.isBefore(bm)) return -1 * direction;
        if (am.isAfter(bm)) return 1 * direction;
        return 0;
      }

      const numKeys = ['Total_Pay_NetTotal'];
      if (numKeys.includes(key)) {
        const an = parseCurrency(av) || 0;
        const bn = parseCurrency(bv) || 0;
        return an === bn ? 0 : an < bn ? -1 * direction : 1 * direction;
      }

      const as = (av ?? '').toString().toLowerCase();
      const bs = (bv ?? '').toString().toLowerCase();
      if (as < bs) return -1 * direction;
      if (as > bs) return 1 * direction;
      return 0;
    });
    return copy;
  }, [filteredItems, sortConfig]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedItems.slice(indexOfFirstItem, indexOfLastItem);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
    setCurrentPage(1);
  };

  return (
    <>
      <Topbar
        breadcrumbs={[
          { label: "จัดซื้อสินค้า", link: "/payment-voucher" },
          { label: name },
        ]}
      />
      <div className="page-header d-flex justify-content-between align-items-center mb-0">
        <Breadcrumbs
          page={name}
          items={[
            { name: "จัดซื้อสินค้า", url: "/payment-voucher" },
            { name: name, url: "/payment-voucher" },
          ]}
        />
      </div>
      <div className="row">
        <div className="col-12">
          <SearchAndFilter
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            searchPlaceholder="ค้นหาเลขเอกสาร, ผู้จำหน่าย, พนักงาน, แผนก..."
            startDate={startDate}
            endDate={endDate}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            showDateFilter={true}
            onAddNew={onPageInsert}
            addButtonText={`สร้าง${name}`}
            showAddButton={true}
            name={name}
            filteredItems={filteredItems}
          />
          <PaymentVoucherTable
            currentItems={currentItems}
            onRowSelected={onRowSelected}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            handlePageChange={handlePageChange}
            dataMasterList={sortedItems}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            getReport={(docNo) => getReport("pay", docNo)}
            statusOptions={statusOptions}
            statusField="Pay_Status"
            onStatusChange={handleStatusChange}
            isStatusEditable={isStatusEditable}
            sortConfig={sortConfig}
            onSort={handleSort}
          />
        </div>
      </div>
      {showPaymentChannelModal && (
        <div
          className={`modal fade ${showPaymentChannelModal ? "show" : ""
            }`}
          tabIndex="-1"
          role="dialog"
          style={{ display: showPaymentChannelModal ? "block" : "none" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">เลือกช่องทางการชำระ</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleClosePaymentModal}
                  disabled={isSavingPayment}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <strong>เลขที่เอกสาร:</strong>{" "}
                  {pendingPaymentContext?.rowData?.Pay_No || "-"}
                </div>
                <div className="mb-3">
                  <strong>ผู้ขาย:</strong>{" "}
                  {pendingPaymentContext?.rowData?.AP_Name || "-"}
                </div>
                <div className="mb-3">
                  <strong>ยอดสุทธิ:</strong>{" "}
                  {formatCurrency(modalDocTotal || 0)} บาท
                </div>
                <div className="btn-group mb-3" role="group">
                  <button
                    type="button"
                    className={`btn ${modalPaymentType.transfer
                      ? "btn-danger"
                      : "btn-outline-secondary"
                      }`}
                    onClick={() => toggleModalPaymentMethod("transfer")}
                    disabled={isSavingPayment}
                  >
                    โอนเงิน
                  </button>
                  <button
                    type="button"
                    className={`btn ${modalPaymentType.cash
                      ? "btn-danger"
                      : "btn-outline-secondary"
                      }`}
                    onClick={() => toggleModalPaymentMethod("cash")}
                    disabled={isSavingPayment}
                  >
                    เงินสด
                  </button>
                  <button
                    type="button"
                    className={`btn ${modalPaymentType.cheque
                      ? "btn-danger"
                      : "btn-outline-secondary"
                      }`}
                    onClick={() => toggleModalPaymentMethod("cheque")}
                    disabled={isSavingPayment}
                  >
                    เช็ค
                  </button>
                </div>

                {modalPaymentType.transfer && (
                  <div
                    className="p-3 border rounded mb-3"
                    style={{ backgroundColor: "#f7f7f7" }}
                  >
                    <div className="row g-3">
                      <div className="col-md-4">
                        <label className="form-label">
                          จำนวนเงิน (โอน)
                        </label>
                        <input
                          type="text"
                          className="form-control text-end"
                          value={modalPaymentAmount.transfer || "0.00"}
                          onFocus={() => handleModalAmountFocus("transfer")}
                          onBlur={() => handleModalAmountBlur("transfer")}
                          onChange={(e) =>
                            handleModalAmountChange("transfer", e.target.value)
                          }
                          disabled={isSavingPayment}
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">วันที่โอน</label>
                        <input
                          type="date"
                          className="form-control"
                          value={modalPayDatetime || ""}
                          onChange={(e) => setModalPayDatetime(e.target.value)}
                          disabled={isSavingPayment}
                        />
                      </div>
                    </div>
                    <div className="row g-2 mt-1">
                      <div className="col-12">
                        <div className="form-check mb-2">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="modalBankInputMode"
                            id="modalBankSelect"
                            value="select"
                            checked={modalBankInputMode === "select"}
                            onChange={() => {
                              setModalBankInputMode("select");
                              setModalBankMasterId("");
                              setModalPayToAccNo("");
                              setModalPayToAccName("");
                              setModalPayToBranch("");
                            }}
                            disabled={isSavingPayment}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="modalBankSelect"
                          >
                            เลือกบัญชีธนาคาร
                          </label>
                        </div>
                        <div className="form-check mb-2">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="modalBankInputMode"
                            id="modalBankManual"
                            value="manual"
                            checked={modalBankInputMode === "manual"}
                            onChange={() => {
                              setModalBankInputMode("manual");
                              setModalBankId("");
                            }}
                            disabled={isSavingPayment}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="modalBankManual"
                          >
                            ระบุเอง
                          </label>
                        </div>
                      </div>
                    </div>
                    {modalBankInputMode === "select" ? (
                      <div className="row g-2 mt-1">
                        <div className="col-md-6">
                          <label className="form-label">บัญชีธนาคาร</label>
                          <select
                            className="form-select"
                            value={modalBankId}
                            onChange={(e) => setModalBankId(e.target.value)}
                            disabled={isSavingPayment}
                            style={{ color: '#000', backgroundColor: '#fff' }}
                          >
                            <option value="">-- เลือกบัญชีธนาคาร --</option>
                            {modalBankList.map((bank) => (
                              <option
                                key={bank.Bank_Id}
                                value={bank.Bank_Id}
                                style={{ color: '#000', backgroundColor: '#fff' }}
                              >
                                {bank.Bank_Account} - {bank.Bank_Name_TH}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="row g-2 mt-1">
                          <div className="col-md-4">
                            <label className="form-label">ธนาคาร</label>
                            <select
                              className="form-select"
                              value={modalBankMasterId}
                              onChange={(e) =>
                                setModalBankMasterId(e.target.value)
                              }
                              disabled={isSavingPayment}
                            >
                              <option value="">-- เลือกธนาคาร --</option>
                              {modalBankMasterList.map((bank) => (
                                <option
                                  key={bank.Bank_Master_Id}
                                  value={bank.Bank_Master_Id}
                                  style={{ color: '#000', backgroundColor: '#fff' }}
                                >
                                  {bank.B_Name_TH}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col-md-4">
                            <label className="form-label">เลขที่บัญชี</label>
                            <input
                              type="text"
                              className="form-control"
                              value={modalPayToAccNo}
                              onChange={(e) => handleModalAccNoChange(e.target.value)}
                              onBlur={handleModalAccNoBlur}
                              disabled={isSavingPayment}
                              placeholder="xxx-xxx-xxxx"
                              inputMode="numeric"
                              maxLength={13}
                              pattern="\\d{3}-\\d{3}-\\d{4}"
                              title="รูปแบบเลขที่บัญชี: xxx-xxx-xxxx"
                            />
                          </div>
                          <div className="col-md-4">
                            <label className="form-label">ชื่อบัญชี</label>
                            <input
                              type="text"
                              className="form-control"
                              value={modalPayToAccName}
                              onChange={(e) =>
                                setModalPayToAccName(e.target.value)
                              }
                              disabled={isSavingPayment}
                            />
                          </div>
                        </div>
                        <div className="row g-2 mt-1">
                          <div className="col-md-4">
                            <label className="form-label">สาขา</label>
                            <input
                              type="text"
                              className="form-control"
                              value={modalPayToBranch}
                              onChange={(e) =>
                                setModalPayToBranch(e.target.value)
                              }
                              disabled={isSavingPayment}
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {modalPaymentType.cash && (
                  <div className="row g-3 mb-3">
                    <div className="col-md-4">
                      <label className="form-label">จำนวนเงิน (เงินสด)</label>
                      <input
                        type="text"
                        className="form-control text-end"
                        value={modalPaymentAmount.cash || "0.00"}
                        onFocus={() => handleModalAmountFocus("cash")}
                        onBlur={() => handleModalAmountBlur("cash")}
                        onChange={(e) =>
                          handleModalAmountChange("cash", e.target.value)
                        }
                        disabled={isSavingPayment}
                      />
                    </div>
                  </div>
                )}

                {modalPaymentType.cheque && (
                  <div
                    className="p-3 border rounded mb-3"
                    style={{ backgroundColor: "#f7f7f7" }}
                  >
                    <div className="row g-3">
                      <div className="col-md-4">
                        <label className="form-label">
                          จำนวนเงิน (เช็ค)
                        </label>
                        <input
                          type="text"
                          className="form-control text-end"
                          value={modalPaymentAmount.cheque || "0.00"}
                          onFocus={() => handleModalAmountFocus("cheque")}
                          onBlur={() => handleModalAmountBlur("cheque")}
                          onChange={(e) =>
                            handleModalAmountChange("cheque", e.target.value)
                          }
                          disabled={isSavingPayment}
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">เลขที่เช็ค</label>
                        <input
                          type="text"
                          className="form-control"
                          value={modalChequeNo}
                          onChange={(e) => setModalChequeNo(e.target.value)}
                          disabled={isSavingPayment}
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">ชื่อผู้ออกเช็ค</label>
                        <input
                          type="text"
                          className="form-control"
                          value={modalChequeName}
                          onChange={(e) => setModalChequeName(e.target.value)}
                          disabled={isSavingPayment}
                        />
                      </div>
                    </div>
                    <div className="row g-2 mt-1">
                      <div className="col-md-4">
                        <label className="form-label">ธนาคาร / สาขา</label>
                        <input
                          type="text"
                          className="form-control"
                          value={modalChequeBranch}
                          onChange={(e) => setModalChequeBranch(e.target.value)}
                          disabled={isSavingPayment}
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">วันที่เช็ค</label>
                        <input
                          type="date"
                          className="form-control"
                          value={modalChequeDate || ""}
                          onChange={(e) => setModalChequeDate(e.target.value)}
                          disabled={isSavingPayment}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {(modalPaymentType.cash ||
                  modalPaymentType.transfer ||
                  modalPaymentType.cheque) && (
                    <small
                      className={`${Math.abs(
                        (getModalSelectedTotal() || 0) - (modalDocTotal || 0)
                      ) < 0.01
                        ? "text-muted"
                        : "text-danger"
                        }`}
                    >
                      รวมช่องทางการชำระ {formatCurrency(getModalSelectedTotal() || 0)} บาท
                      {Math.abs(
                        (getModalSelectedTotal() || 0) - (modalDocTotal || 0)
                      ) >= 0.01 &&
                        ` (ยอดสุทธิ ${formatCurrency(modalDocTotal || 0)} บาท)`}
                    </small>
                  )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handlePaymentModalConfirm}
                  disabled={isSavingPayment}
                >
                  {isSavingPayment ? (
                    <>
                      <i className="fa fa-spinner fa-spin me-2"></i>
                      กำลังบันทึก...
                    </>
                  ) : (
                    "บันทึก & อนุมัติ"
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={handleClosePaymentModal}
                  disabled={isSavingPayment}
                >
                  ยกเลิก
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showPaymentChannelModal && (
        <div className="modal-backdrop fade show"></div>
      )}
    </>
  );
}

export default Main;
